/**
 * PdfExportModal — modale de sélection des sections à inclure dans
 * l'export PDF. Redesign v2 :
 *
 *   - Adopte le design system (Card, Pill, shadow-pop, hairline,
 *     rounded-xl2, lime-300 accents).
 *   - Sections collapsées par défaut avec compteur "N/M" + chevron.
 *   - Chaque section a une master-checkbox (cocher/décocher tout le
 *     groupe d'un coup) + expand pour accéder au détail par block.
 *   - "Mode client" mis en évidence avec un fond lime-50 léger pour le
 *     signaler comme option de rendu (pas une section sélectionnable).
 *
 * Flow inchangé : lit Firestore à l'ouverture, persiste au click
 * "Télécharger" puis appelle onConfirm avec l'état à jour.
 */

import { useEffect, useMemo, useState } from "react";
import type { FicheDoc } from "../lib/fiches";
import { Button, Card, Pill } from "../components/ui";
import { Ic } from "../components/icons";
import {
  loadPdfIncludeState,
  savePdfIncludeState,
  defaultPdfIncludeState,
  type PdfIncludeState,
  type FicheSectionKey,
  type EnvBlockKey,
  type AiBlockKey,
} from "./pdfIncludeState";

// ─── Configuration des groupes ───────────────────────────────────────────
// Chaque entrée du tableau d'un groupe définit un toggle. Les `desc` ne
// s'affichent qu'en mode expand. Les `subgroup` servent à intercaler
// les libellés "CONTRAINTES / QUOTIDIEN / …" dans le groupe Environnement.

type ToggleKey =
  | { kind: "fiche"; key: FicheSectionKey }
  | { kind: "env"; key: EnvBlockKey }
  | { kind: "ai"; key: AiBlockKey };

type ToggleItem = {
  id: ToggleKey;
  label: string;
  desc?: string;
  subgroup?: string; // "CONTRAINTES" | "QUOTIDIEN" | "CONNECTIVITÉ" | "MARCHÉ LOCAL"
};

const FICHE_ITEMS: ToggleItem[] = [
  {
    id: { kind: "fiche", key: "bien" },
    label: "Le bien actuel",
    desc: "Adresse, parcelle, caractéristiques de l'existant",
  },
  {
    id: { kind: "fiche", key: "reglement" },
    label: "Règlement PLU",
    desc: "Règles quantifiées + destinations autorisées/interdites",
  },
  {
    id: { kind: "fiche", key: "risques" },
    label: "Servitudes & risques",
    desc: "Rapport Géorisques",
  },
  {
    id: { kind: "fiche", key: "dvf" },
    label: "Comparables DVF",
    desc: "Transactions récentes + médiane €/m²",
  },
];

const ENV_ITEMS: ToggleItem[] = [
  { id: { kind: "env", key: "abf" }, label: "Protections ABF / MH", subgroup: "CONTRAINTES" },
  { id: { kind: "env", key: "bruit" }, label: "Bruit aéronautique", subgroup: "CONTRAINTES" },
  { id: { kind: "env", key: "ecoles" }, label: "Écoles & éducation", subgroup: "QUOTIDIEN" },
  { id: { kind: "env", key: "commerces" }, label: "Commerces & services", subgroup: "QUOTIDIEN" },
  { id: { kind: "env", key: "transports" }, label: "Transports en commun", subgroup: "QUOTIDIEN" },
  { id: { kind: "env", key: "arcep" }, label: "Couverture fibre & internet", subgroup: "CONNECTIVITÉ" },
  { id: { kind: "env", key: "dpe" }, label: "DPE du voisinage", subgroup: "MARCHÉ LOCAL" },
  { id: { kind: "env", key: "filosofi" }, label: "Profil socio-démographique", subgroup: "MARCHÉ LOCAL" },
  { id: { kind: "env", key: "loyers" }, label: "Loyers de référence", subgroup: "MARCHÉ LOCAL" },
  { id: { kind: "env", key: "priceHistory" }, label: "Évolution des prix au m²", subgroup: "MARCHÉ LOCAL" },
];

const AI_ITEMS: ToggleItem[] = [
  {
    id: { kind: "ai", key: "synthese" },
    label: "Synthèse exécutive",
    desc: "Lecture experte en prose",
  },
  {
    id: { kind: "ai", key: "redFlags" },
    label: "Points de vigilance",
    desc: "Red flags triés par sévérité",
  },
  {
    id: { kind: "ai", key: "estimation" },
    label: "Estimation de prix",
    desc: "Fourchette + base DVF + ajustements",
  },
  {
    id: { kind: "ai", key: "potentiel" },
    label: "Potentiel d'agrandissement",
    desc: "Emprise résiduelle + recommandations",
  },
  {
    id: { kind: "ai", key: "visuel" },
    label: "Analyse visuelle",
    desc: "Style, état apparent, équipements",
  },
  {
    id: { kind: "ai", key: "priceTrend" },
    label: "Tendance du marché",
    desc: "Contexte commune",
  },
];

// ─── Props ───────────────────────────────────────────────────────────────

export type PdfExportModalProps = {
  fiche: FicheDoc;
  open: boolean;
  onClose: () => void;
  onConfirm: (include: PdfIncludeState) => Promise<void>;
};

// ─── Modal ───────────────────────────────────────────────────────────────

export function PdfExportModal({
  fiche,
  open,
  onClose,
  onConfirm,
}: PdfExportModalProps) {
  const [state, setState] = useState<PdfIncludeState>(defaultPdfIncludeState());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Sections toutes collapsées par défaut — l'agent voit d'abord une
  // vue d'ensemble propre. Il n'expand que si besoin de décocher un
  // élément précis.
  const [expanded, setExpanded] = useState<{
    fiche: boolean;
    env: boolean;
    ai: boolean;
  }>({ fiche: false, env: false, ai: false });

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    loadPdfIncludeState(fiche.id)
      .then((loaded) => {
        if (!cancelled) {
          setState(loaded);
          setLoading(false);
        }
      })
      .catch((e) => {
        console.error("[pdf modal] failed to load state", e);
        if (!cancelled) {
          setState(defaultPdfIncludeState());
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, fiche.id]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !saving) onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, saving, onClose]);

  // Comptes pour les master-checkboxes et le footer.
  const counts = useMemo(() => {
    const fiche = Object.values(state.fiche).filter(Boolean).length;
    const env = Object.values(state.env).filter(Boolean).length;
    const ai = Object.values(state.ai).filter(Boolean).length;
    return {
      fiche: { selected: fiche, total: FICHE_ITEMS.length },
      env: { selected: env, total: ENV_ITEMS.length },
      ai: { selected: ai, total: AI_ITEMS.length },
      totalSelected: fiche + env + ai,
      totalAll: FICHE_ITEMS.length + ENV_ITEMS.length + AI_ITEMS.length,
    };
  }, [state]);

  if (!open) return null;

  const setItem = (id: ToggleKey, value: boolean) => {
    setState((prev) => {
      if (id.kind === "fiche") {
        return { ...prev, fiche: { ...prev.fiche, [id.key]: value } };
      }
      if (id.kind === "env") {
        return { ...prev, env: { ...prev.env, [id.key]: value } };
      }
      return { ...prev, ai: { ...prev.ai, [id.key]: value } };
    });
  };

  const setGroup = (group: "fiche" | "env" | "ai", value: boolean) => {
    setState((prev) => {
      const items =
        group === "fiche" ? FICHE_ITEMS : group === "env" ? ENV_ITEMS : AI_ITEMS;
      const updated: Record<string, boolean> = {};
      for (const it of items) {
        if (it.id.kind === group) updated[it.id.key] = value;
      }
      return { ...prev, [group]: updated } as PdfIncludeState;
    });
  };

  const toggleAll = (value: boolean) => {
    setGroup("fiche", value);
    setGroup("env", value);
    setGroup("ai", value);
  };

  const handleConfirm = async () => {
    if (counts.totalSelected === 0) return;
    setSaving(true);
    try {
      await savePdfIncludeState(fiche.id, state);
      await onConfirm(state);
      onClose();
    } catch (e) {
      console.error("[pdf modal] save/generate failed", e);
      alert(
        `La génération du PDF a échoué : ${e instanceof Error ? e.message : String(e)}`,
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-ink-900/40 backdrop-blur-sm grid place-items-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !saving) onClose();
      }}
    >
      <Card
        className="w-full max-w-[560px] max-h-[88vh] p-0 shadow-pop flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header — même pattern que SignInDialog : Pill lime + close X */}
        <div className="px-7 pt-6 pb-5 border-b border-ink-100">
          <div className="flex items-center justify-between mb-1">
            <Pill tone="lime" icon={<Ic.Download size={12} />}>
              Export PDF
            </Pill>
            <button
              onClick={onClose}
              disabled={saving}
              className="w-8 h-8 rounded-full hover:bg-ink-100 grid place-items-center text-ink-500 disabled:opacity-50"
              aria-label="Fermer"
            >
              <Ic.Cross size={14} />
            </button>
          </div>
          <h2 className="mt-3 text-[22px] font-semibold tracking-tight text-ink-900">
            Sections à inclure
          </h2>
          <p className="mt-1.5 text-[13px] text-ink-500 leading-relaxed">
            Vos choix sont sauvegardés pour les prochains exports de
            cette fiche.
          </p>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-7 py-5">
          {loading ? (
            <ModalSkeleton />
          ) : (
            <div className="space-y-3">
              {/* Toggle mode client — card en lime-50 léger */}
              <label className="flex items-start gap-3 py-3 px-4 rounded-xl2 bg-lime-50 hairline cursor-pointer hover:bg-lime-100/60 transition">
                <Checkbox
                  checked={state.clientMode}
                  onChange={(v) =>
                    setState((prev) => ({ ...prev, clientMode: v }))
                  }
                />
                <div className="flex-1 min-w-0">
                  <div className="text-[13.5px] font-semibold text-ink-900">
                    Mode client — masquer les références à l'IA
                  </div>
                  <div className="text-[12px] text-ink-600 leading-relaxed mt-0.5">
                    La section IA devient « Analyse », les mentions de
                    date/modèle sont retirées. À activer avant d'envoyer
                    le PDF à un client.
                  </div>
                </div>
              </label>

              {/* Les 3 groupes collapsibles */}
              <GroupCard
                title="Fiche"
                desc="Données de base du bien et du règlement"
                count={counts.fiche}
                expanded={expanded.fiche}
                onToggleExpand={() =>
                  setExpanded((p) => ({ ...p, fiche: !p.fiche }))
                }
                onToggleAll={(v) => setGroup("fiche", v)}
              >
                {FICHE_ITEMS.map((it) => (
                  <ItemRow
                    key={`${it.id.kind}-${it.id.key}`}
                    item={it}
                    checked={state.fiche[it.id.key as FicheSectionKey]}
                    onChange={(v) => setItem(it.id, v)}
                  />
                ))}
              </GroupCard>

              <GroupCard
                title="Environnement"
                desc="10 sources publiques autour du bien"
                count={counts.env}
                expanded={expanded.env}
                onToggleExpand={() =>
                  setExpanded((p) => ({ ...p, env: !p.env }))
                }
                onToggleAll={(v) => setGroup("env", v)}
              >
                {ENV_ITEMS.map((it, i) => {
                  // Intercale un label sub-group avant la 1re occurrence
                  // de chaque (cohérent avec la structure Env du PDF).
                  const prevSubgroup = i > 0 ? ENV_ITEMS[i - 1].subgroup : null;
                  const showSubHeader =
                    it.subgroup && it.subgroup !== prevSubgroup;
                  return (
                    <div key={`${it.id.kind}-${it.id.key}`}>
                      {showSubHeader ? (
                        <div className="mt-3 mb-1 px-2 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-ink-500">
                          {it.subgroup}
                        </div>
                      ) : null}
                      <ItemRow
                        item={it}
                        checked={state.env[it.id.key as EnvBlockKey]}
                        onChange={(v) => setItem(it.id, v)}
                      />
                    </div>
                  );
                })}
              </GroupCard>

              <GroupCard
                title="Analyse IA"
                desc="Lectures expertes générées automatiquement"
                count={counts.ai}
                expanded={expanded.ai}
                onToggleExpand={() =>
                  setExpanded((p) => ({ ...p, ai: !p.ai }))
                }
                onToggleAll={(v) => setGroup("ai", v)}
              >
                {AI_ITEMS.map((it) => (
                  <ItemRow
                    key={`${it.id.kind}-${it.id.key}`}
                    item={it}
                    checked={state.ai[it.id.key as AiBlockKey]}
                    onChange={(v) => setItem(it.id, v)}
                  />
                ))}
              </GroupCard>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-7 py-4 border-t border-ink-100 bg-ink-50/60 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3 text-[12px] text-ink-600">
            <span className="font-medium">
              {counts.totalSelected} / {counts.totalAll}
            </span>
            <span className="text-ink-300">·</span>
            <button
              type="button"
              onClick={() => toggleAll(true)}
              disabled={loading || saving}
              className="text-ink-600 underline-offset-2 hover:underline disabled:opacity-50"
            >
              tout cocher
            </button>
            <span className="text-ink-300">·</span>
            <button
              type="button"
              onClick={() => toggleAll(false)}
              disabled={loading || saving}
              className="text-ink-600 underline-offset-2 hover:underline disabled:opacity-50"
            >
              tout décocher
            </button>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={onClose} disabled={saving}>
              Annuler
            </Button>
            <Button
              variant="primary"
              size="sm"
              icon={<Ic.Download size={13} />}
              onClick={handleConfirm}
              disabled={loading || saving || counts.totalSelected === 0}
            >
              {saving ? "Génération…" : "Télécharger"}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

// ─── GroupCard — section collapsible avec master checkbox ────────────────

function GroupCard({
  title,
  desc,
  count,
  expanded,
  onToggleExpand,
  onToggleAll,
  children,
}: {
  title: string;
  desc: string;
  count: { selected: number; total: number };
  expanded: boolean;
  onToggleExpand: () => void;
  onToggleAll: (v: boolean) => void;
  children: React.ReactNode;
}) {
  const state: "all" | "none" | "some" =
    count.selected === count.total
      ? "all"
      : count.selected === 0
        ? "none"
        : "some";

  return (
    <div className="rounded-xl2 hairline bg-paper overflow-hidden">
      {/* Header ligne — master checkbox + label + compteur + chevron */}
      <div className="flex items-center gap-3 px-4 py-3">
        <Checkbox
          checked={state === "all"}
          indeterminate={state === "some"}
          onChange={(v) => onToggleAll(v)}
        />
        <button
          type="button"
          onClick={onToggleExpand}
          className="flex-1 flex items-center justify-between text-left gap-3 min-w-0"
        >
          <div className="min-w-0">
            <div className="text-[14px] font-semibold text-ink-900">
              {title}
            </div>
            <div className="text-[12px] text-ink-500 leading-tight mt-0.5">
              {desc}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span
              className={`text-[12px] tabular-nums font-medium ${
                state === "all"
                  ? "text-ink-700"
                  : state === "none"
                    ? "text-ink-400"
                    : "text-ink-700"
              }`}
            >
              {count.selected}/{count.total}
            </span>
            <Ic.Arrow
              size={12}
              className={`text-ink-400 transition-transform ${expanded ? "rotate-90" : ""}`}
            />
          </div>
        </button>
      </div>

      {/* Contenu déplié */}
      {expanded ? (
        <div className="border-t border-ink-100 bg-ink-50/40 px-2 py-2 space-y-0.5">
          {children}
        </div>
      ) : null}
    </div>
  );
}

// ─── ItemRow — une entrée individuelle dans une section expandée ──────

function ItemRow({
  item,
  checked,
  onChange,
}: {
  item: ToggleItem;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-start gap-3 py-2 px-3 rounded-lg hover:bg-paper cursor-pointer transition">
      <Checkbox checked={checked} onChange={onChange} />
      <div className="flex-1 min-w-0">
        <div className="text-[13px] text-ink-900">{item.label}</div>
        {item.desc ? (
          <div className="text-[11.5px] text-ink-500 leading-tight mt-0.5">
            {item.desc}
          </div>
        ) : null}
      </div>
    </label>
  );
}

// ─── Custom checkbox ────────────────────────────────────────────────────
// HTML <input type=checkbox> ne permet pas de styliser proprement
// l'état indeterminate en CSS pur. On dessine donc la case à la main,
// avec un vrai input caché au clavier pour l'accessibilité.

function Checkbox({
  checked,
  indeterminate = false,
  onChange,
}: {
  checked: boolean;
  indeterminate?: boolean;
  onChange: (v: boolean) => void;
}) {
  const filled = checked || indeterminate;
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={indeterminate ? "mixed" : checked}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onChange(!checked);
      }}
      className={`relative mt-0.5 w-[18px] h-[18px] rounded-[5px] shrink-0 transition ${
        filled
          ? "bg-ink-900 border border-ink-900"
          : "bg-paper border border-ink-300 hover:border-ink-500"
      }`}
    >
      {indeterminate ? (
        // Dash centré
        <span className="absolute inset-x-[3px] top-1/2 -translate-y-1/2 h-[2px] bg-lime-300 rounded" />
      ) : checked ? (
        // Check en lime-300 sur fond ink-900
        <svg
          className="absolute inset-0 m-auto text-lime-300"
          width="11"
          height="11"
          viewBox="0 0 12 12"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="2.5 6 5 8.5 9.5 3.5" />
        </svg>
      ) : null}
    </button>
  );
}

// ─── Skeleton ────────────────────────────────────────────────────────────

function ModalSkeleton() {
  return (
    <div className="space-y-3 animate-pulse">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="rounded-xl2 hairline p-4">
          <div className="flex items-center gap-3">
            <div className="w-[18px] h-[18px] bg-ink-200 rounded" />
            <div className="flex-1">
              <div className="h-3 bg-ink-200 rounded w-32 mb-2" />
              <div className="h-2.5 bg-ink-100 rounded w-48" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
